import React, { useState, useEffect } from 'react';
import { X, ExternalLink, MessageSquare, Globe, FileText, StickyNote } from 'lucide-react';
import { useAppearance } from '../contexts/AppearanceContext';
import { getContextSpace } from '../services/contextSpacesApi';

const CitationPreview = ({ citationTitle, contextSpaceId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sourceData, setSourceData] = useState(null);
  const { isSuppleMode } = useAppearance();

  useEffect(() => {
    const findSourceData = async () => {
      if (!contextSpaceId || !citationTitle) return;

      try {
        setLoading(true);
        const contextSpace = await getContextSpace(contextSpaceId);
        
        // Look for the citation in resources
        let foundSource = contextSpace.resources?.find(resource => 
          resource.title.includes(citationTitle) || citationTitle.includes(resource.title)
        );

        if (foundSource) {
          setSourceData({
            type: 'resource',
            ...foundSource
          });
        } else {
          // Look for the citation in conversations
          foundSource = contextSpace.conversations?.find(item => 
            item.conversation.title.includes(citationTitle) || citationTitle.includes(item.conversation.title)
          );

          if (foundSource) {
            setSourceData({
              type: 'conversation',
              ...foundSource.conversation
            });
          } else {
            setError('Source not found');
          }
        }
      } catch (err) {
        console.error('Failed to load source data:', err);
        setError('Failed to load source data');
      } finally {
        setLoading(false);
      }
    };

    findSourceData();
  }, [citationTitle, contextSpaceId]);

  const getSourceIcon = (type, crawlStatus) => {
    if (type === 'conversation') {
      return <MessageSquare className="w-4 h-4" />;
    }
    
    switch (type) {
      case 'FILE':
        return <FileText className="w-4 h-4" />;
      case 'URL':
        return <Globe className="w-4 h-4" />;
      case 'NOTE':
      case 'TEXT':
        return <StickyNote className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading source...</span>
        </div>
      );
    }

    if (error || !sourceData) {
      return (
        <div className="text-center py-8">
          <div className="text-red-500 mb-2">⚠️ {error || 'Source not found'}</div>
          <p className="text-gray-500 text-sm">The referenced source could not be located.</p>
        </div>
      );
    }

    if (sourceData.type === 'conversation') {
      return (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold">{sourceData.title}</h3>
          </div>
          
          <div className="space-y-3">
            {sourceData.messages?.slice(-5).map((message, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg ${
                  message.role === 'user' 
                    ? isSuppleMode 
                      ? 'bg-purple-500/20 border-l-4 border-purple-300'
                      : 'bg-blue-50 border-l-4 border-blue-300'
                    : isSuppleMode
                      ? 'bg-gray-500/20 border-l-4 border-gray-300'
                      : 'bg-gray-50 border-l-4 border-gray-300'
                }`}
              >
                <div className="text-sm font-medium mb-1 capitalize">
                  {message.role === 'user' ? 'User' : 'Assistant'}
                </div>
                <div className="text-sm line-clamp-3">
                  {message.content.length > 300 
                    ? message.content.substring(0, 300) + '...'
                    : message.content
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Resource content
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          {getSourceIcon(sourceData.type, sourceData.crawlStatus)}
          <h3 className="text-lg font-semibold">{sourceData.title}</h3>
          {sourceData.url && (
            <button
              onClick={() => window.open(sourceData.url, '_blank')}
              className="ml-auto p-1 text-gray-500 hover:text-blue-600"
              title="Open original source"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>

        {sourceData.description && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">{sourceData.description}</p>
          </div>
        )}

        {sourceData.url && (
          <div className="mb-4">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">URL</label>
            <div className="mt-1">
              <a
                href={sourceData.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm break-all"
              >
                {sourceData.url}
              </a>
            </div>
          </div>
        )}

        {sourceData.content && (
          <div className="mb-4">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Content Preview</label>
            <div className="mt-2 p-4 bg-gray-50 rounded-lg max-h-60 overflow-y-auto">
              <pre className="text-sm whitespace-pre-wrap font-mono">
                {sourceData.content.length > 1000 
                  ? sourceData.content.substring(0, 1000) + '\n\n... (content truncated)'
                  : sourceData.content
                }
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-lg shadow-xl ${
        isSuppleMode ? 'supple-card-glass backdrop-blur-xl' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isSuppleMode ? 'supple-border-subtle' : 'border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold ${
            isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
          }`}>
            Citation Preview
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'supple-text-muted hover:supple-text-primary hover:supple-card-glass-hover' 
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default CitationPreview;